// One-time account setup (2026-10-19). A legacy colour + PIN account lands
// here on its first login after the username rollout: choose a username,
// set a policy password, and — Managers/Admins only — add an email if there
// is none on file. The BE decides what is outstanding (login response
// `accountSetup`), this screen asks only for that.
//
// One card, not a stepper: the browser offers to SAVE the credential only
// when the username and the new password are submitted by the same <form>,
// and that saved credential is what Face ID / fingerprint / Windows Hello
// fills on the next login — when the Settings › Login screen switch allows
// saving at all. It is off by default (components/login/savedLogins), and
// then neither field is offered to the browser as a credential.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import { apiFetch } from "../api/apiFetch";
import { isManagerPlus, readUserPosition } from "../access/positions";
import LanguageToggle from "../components/LanguageToggle";
import PasswordFields from "../components/PasswordFields";
import { PASSWORD_MANAGER_OPT_OUT, useSavedLoginsAllowed } from "../components/login/savedLogins";
import { useUsernameAvailability } from "../hooks/useUsernameAvailability";
import { useIsPhone } from "../hooks/useIsPhone";
import { isPolicyCompliant, passwordPolicyFor, type PasswordContext } from "../components/helpers/passwordPolicy";
import { normaliseUsername, usernameErrorKey, USERNAME_MAX } from "../components/helpers/usernameRules";
import { isValidEmail } from "../components/helpers/emailFormat";
import { clearAccountSetup, clearRequirePasswordChange, readAccountSetup, readRequirePasswordChange } from "../components/helpers/authFlags";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import "../styles/change-password.css";

const readActor = (): { uid?: number; name?: string; warehouse?: string } => {
    const actor: { uid?: number; name?: string; warehouse?: string } = {};
    try {
        const raw = window.localStorage.getItem("user");
        if (raw) {
            const u = JSON.parse(raw);
            if (typeof u?.uid === "number") actor.uid = u.uid;
            if (typeof u?.name === "string") actor.name = u.name;
            if (typeof u?.warehouse === "string") actor.warehouse = u.warehouse;
        }
    } catch { /* ignore */ }
    return actor;
};

const patchStoredUser = (patch: Record<string, unknown>) => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return;
        window.localStorage.setItem("user", JSON.stringify({ ...JSON.parse(raw), ...patch }));
    } catch { /* ignore */ }
};

const AccountSetup = () => {
    usePageTitle("account_setup_title");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const flags = useMemo(() => readAccountSetup(), []);
    // Phones get the short hints — every line of copy costs a scroll on a handheld.
    const short = useIsPhone();
    const hint = (key: string) => t(short ? `${key}_short` : key);
    const position = useMemo(() => readUserPosition(), []);
    const managerPlus = isManagerPlus(position);
    const policy = useMemo(() => passwordPolicyFor(position), [position]);
    const landing = managerPlus ? "/admin" : "/";
    const savedLogins = useSavedLoginsAllowed();

    const needUsername = flags?.username === true;
    const needEmail = flags?.email === true;
    const needPassword = flags?.password === true || needUsername;
    // Username / email on the left, password on the right, on desktop (one
    // column on a phone) so the whole form fits a screen without scrolling.
    const twoColumns = needPassword && (needUsername || needEmail);

    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    // A rejection from the POST, shown inline under the username field.
    const [serverUsernameCode, setServerUsernameCode] = useState<string | null>(null);

    const availability = useUsernameAvailability(username, { enabled: needUsername });
    // The password may not contain the handle being chosen (or the one already set) or the display name.
    const passwordContext: PasswordContext = {
        username: needUsername ? username : (flags?.currentUsername ?? null),
        name: readActor().name ?? null,
    };

    // Forced-only screen: not signed in → login; nothing outstanding →
    // wherever the user was going (a stale bookmark must not show a
    // half-empty form).
    useEffect(() => {
        const isLoggedIn = window.localStorage.getItem("isLoggedIn") === "true";
        if (!isLoggedIn) { navigate("/login", { replace: true }); return; }
        if (!flags) navigate(readRequirePasswordChange() ? "/change-password" : landing, { replace: true });
    }, [flags, landing, navigate]);

    const usernameOk = !needUsername || availability.status === "available" || availability.status === "unknown";
    const emailOk = !needEmail || isValidEmail(email);
    const passwordOk = !needPassword || (isPolicyCompliant(newPassword, policy, passwordContext) && newPassword === confirmPassword);
    const canSubmit = usernameOk && emailOk && passwordOk && !submitting && serverUsernameCode === null;

    const availabilityLine = () => {
        if (!needUsername) return null;
        const code = serverUsernameCode ?? (availability.status === "invalid" || availability.status === "unavailable" ? availability.code : null);
        if (code) {
            return (
                <span className="change-password-availability change-password-availability--bad" role="alert">
                    <X size={14} aria-hidden="true" /> {t(usernameErrorKey(code))}
                </span>
            );
        }
        if (availability.status === "checking") {
            return (
                <span className="change-password-availability change-password-availability--checking">
                    <Loader2 size={14} aria-hidden="true" /> {t("username_checking")}
                </span>
            );
        }
        if (availability.status === "available") {
            return (
                <span className="change-password-availability change-password-availability--ok">
                    <Check size={14} aria-hidden="true" /> {t("username_available")}
                </span>
            );
        }
        return <span className="change-password-availability" />;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const body: Record<string, unknown> = { ...readActor() };
            if (needUsername) body.username = username;
            if (needEmail) body.email = email.trim();
            if (needPassword) body.newPassword = newPassword;
            const res = await apiFetch(`/login/user/account-setup`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({} as any));

            if (res.status === 401) {
                // apiFetch never treats a /login/* 401 as a dead session —
                // handle it here: the session is gone, start over.
                await logoutProcessor();
                navigate("/login", { replace: true });
                return;
            }
            if (res.ok || data?.code === "already_set") {
                // already_set = another device (or an admin) finished it
                // first; either way there is nothing left to do here.
                clearAccountSetup();
                if (needPassword && res.ok) clearRequirePasswordChange();
                patchStoredUser({
                    ...(needUsername ? { username: data?.loginUsername ?? username } : {}),
                    ...(needEmail ? { email: email.trim() } : {}),
                });
                toast.success(t("account_setup_done"), { toastId: "account-setup-ok" });
                // Do NOT reset the fields first — the browser's "save
                // password?" prompt pairs with the submitted values.
                navigate(landing, { replace: true });
                return;
            }
            const usernameCodes = ["required", "too_short", "too_long", "invalid_chars", "reserved", "profanity", "taken"];
            if (typeof data?.code === "string" && usernameCodes.includes(data.code)) {
                setServerUsernameCode(data.code);
                return;
            }
            toast.error(data?.message ?? t("account_setup_failed"), { toastId: "account-setup-err" });
        } catch {
            toast.error(t("account_setup_failed"), { toastId: "account-setup-err" });
        } finally {
            setSubmitting(false);
        }
    };

    if (!flags) return null;

    return (
        <div className="change-password-page">
            <div className={`change-password-card animate-slideUp${twoColumns ? " change-password-card--wide" : ""}`}>
                <LanguageToggle className="change-password-language-toggle" />
                <h1 className="change-password-title">{t("account_setup_title")}</h1>
                <p className="change-password-subtitle">{hint("account_setup_subtitle")}</p>

                <form className="change-password-form" onSubmit={handleSubmit}>
                    <div className={`change-password-grid${twoColumns ? " change-password-grid--two" : ""}`}>
                    <div className="change-password-col">
                    <div className="change-password-section">
                        <h2 className="change-password-section-title">{t("account_setup_username_section")}</h2>
                        <div className="change-password-field">
                            <span className="change-password-label-row">
                                <label htmlFor="account-setup-username">
                                    {needUsername ? t("account_setup_username_label") : t("users_col_username")}
                                </label>
                            </span>
                            <input
                                id="account-setup-username"
                                className={`change-password-input${needUsername ? "" : " change-password-input--readonly"}`}
                                type="text"
                                {...(savedLogins
                                    ? { name: "username", autoComplete: "username" }
                                    : { autoComplete: "off", ...PASSWORD_MANAGER_OPT_OUT })}
                                inputMode="text"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                enterKeyHint="next"
                                maxLength={USERNAME_MAX}
                                value={needUsername ? username : (flags.currentUsername ?? "")}
                                readOnly={!needUsername}
                                onChange={(e) => { setUsername(normaliseUsername(e.target.value)); setServerUsernameCode(null); }}
                                disabled={submitting}
                                autoFocus={needUsername}
                            />
                            {availabilityLine()}
                        </div>
                        <p className="change-password-hint">
                            {needUsername ? hint("account_setup_username_hint") : t("account_setup_username_fixed_hint")}
                        </p>
                    </div>

                    {needEmail && (
                        <div className="change-password-section">
                            <h2 className="change-password-section-title">{t("account_setup_email_section")}</h2>
                            <div className="change-password-field">
                                <span className="change-password-label-row">
                                    <label htmlFor="account-setup-email">{t("account_setup_email_label")}</label>
                                </span>
                                <input
                                    id="account-setup-email"
                                    className="change-password-input"
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    placeholder={t("email_placeholder")}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={submitting}
                                    autoFocus={!needUsername}
                                />
                                {email.trim() !== "" && !isValidEmail(email) && (
                                    <span className="change-password-mismatch">{t("login_link_invalid_email")}</span>
                                )}
                            </div>
                            <p className="change-password-hint">{hint("account_setup_email_hint")}</p>
                        </div>
                    )}

                    </div>

                    {needPassword && (
                        <div className="change-password-col">
                        <div className="change-password-section">
                            <h2 className="change-password-section-title">{t("account_setup_password_section")}</h2>
                            {/* The "two words and a number" hint only fits the memorable
                                policy; strong positions (incl. Customer Service) get the checklist alone. */}
                            {policy.style === "memorable" && (
                                <p className="change-password-hint">{hint("account_setup_password_hint")}</p>
                            )}
                            <PasswordFields
                                variant="dark"
                                policy={policy}
                                context={passwordContext}
                                newPassword={newPassword}
                                confirmPassword={confirmPassword}
                                onNewPasswordChange={setNewPassword}
                                onConfirmPasswordChange={setConfirmPassword}
                                showGenerator
                                autoCompleteMode="new-password"
                                allowSave={savedLogins}
                                disabled={submitting}
                            />
                        </div>
                        </div>
                    )}
                    </div>

                    <div className="change-password-actions">
                        <span />
                        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                            {submitting ? t("saving") : t("account_setup_submit")}
                        </button>
                    </div>
                    <button
                        type="button"
                        className="change-password-exit"
                        onClick={async () => { await logoutProcessor(); navigate("/login", { replace: true }); }}
                        disabled={submitting}
                    >
                        {t("logout")}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AccountSetup;
